import { useState, useEffect } from 'react';
import * as echarts from 'echarts';
import { notification } from 'tea-component';
import { getOverviewStaffRate, getStudioStaffRate } from '@sg/services/index';
import { chartAllColors, chartGradientColors } from '@sg/utils/useChartOptions';
import {
  resolveActualForecastStyle,
  resolveMetricTooltipDate,
} from '../../utils/metricChartTooltip';
import {
  filterValidAxisRows,
  normalizeMetricAxisFields,
  type TMetricScope,
} from '../../utils/normalizeMetricAxisRows';

const toStaffRateNumberOrNull = (value: any): number | null => {
  if (value === null || value === undefined) return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

export type TStaffRateScopeOptions =
  | { scope: 'studio'; studioId: string }
  | { scope: 'portfolio' };

export const useStaffRateData = (
  scopeOptions: TStaffRateScopeOptions,
  activeDateTypeSegment: string,
  formatMessage?: (descriptor: { id: string }) => string,
  selectedMonth?: string | null,
) => {
  const [staffRateData, setStaffRateData] = useState<any>({});
  const [staffRateTableData, setStaffRateTableData] = useState<any[]>([]);
  const [staffRateUpdateTime, setStaffRateUpdateTime] = useState<string>('');
  const [staffRateLoading, setStaffRateLoading] = useState<boolean>(false);

  const scope: TMetricScope = scopeOptions.scope;
  const studioId = scopeOptions.scope === 'studio' ? scopeOptions.studioId : '';

  useEffect(() => {
    if (scope === 'studio' && !studioId) {
      setStaffRateData({});
      setStaffRateTableData([]);
      setStaffRateUpdateTime('');
      setStaffRateLoading(false);
      return;
    }

    const fetchStaffRate = async () => {
      setStaffRateLoading(true);
      // Clear before fetch so a null datatime from the new segment does not keep the old value.
      setStaffRateUpdateTime('');
      try {
        const yearMode = activeDateTypeSegment === 'year';
        const basePayload: {
          view_mode: 'by_month' | 'by_year';
          datatime?: string;
        } = {
          view_mode: yearMode ? 'by_year' : 'by_month',
        };
        // Chart/table view uses datatime; download uses start_date/end_date instead.
        if (selectedMonth) {
          basePayload.datatime = selectedMonth;
        }

        const res = scope === 'portfolio'
          ? await getOverviewStaffRate(basePayload)
          : await getStudioStaffRate({
            ...basePayload,
            studio_id: studioId,
          });
        const responseData = res?.data?.data || [];
        const responseDatatime = res?.data?.datatime ?? '';
        setStaffRateUpdateTime(responseDatatime);
        const staffRateRows = filterValidAxisRows(
          responseData.map((item: any) => {
            const axis = normalizeMetricAxisFields(item, scope);
            return {
              ...axis,
              // Tooltip date; empty row date falls back to response datatime.
              date: resolveMetricTooltipDate(item?.date, item?.month, responseDatatime),
              title: item?.studio_name,
              // isActual: item?.is_actual,
              source: item?.source,
              staff_rate: toStaffRateNumberOrNull(item?.staff_rate),
            };
          }),
        );

        const orderedRows = scope === 'studio'
          ? [...staffRateRows].sort((a: any, b: any) => a.key.localeCompare(b.key))
          : staffRateRows;

        setStaffRateTableData(orderedRows);

        const xAxisData = orderedRows.map((item: any) => item.label);
        const seriesName = formatMessage
          ? formatMessage({ id: 'dbs_studios_staff_rate' })
          : 'Staff Rate';
        // Same gradient index as Gross Revenue in useFinancialsData.
        const barGradient = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: chartGradientColors[1]?.[1] },
          { offset: 1, color: chartGradientColors[1]?.[0] },
        ]);

        setStaffRateData({
          bar: {
            xAxisData,
            type: 'bar',
            seriesData: [{
              name: seriesName,
              type: 'bar',
              barWidth: '40%',
              color: chartAllColors[1],
              itemStyle: {
                color: barGradient,
              },
              data: orderedRows.map((row: any) => {
                const value = row.staff_rate;
                const { useForecastStyle, tooltipHeaderRight } = resolveActualForecastStyle({
                  // isActual: row.isActual,
                  source: row.source,
                });
                const numericValue = value === null || value === undefined
                  ? NaN
                  : Number(value);
                // Always return an object (even when value is null) so tooltips
                // can still show title / date / Actual|Forecast.
                return {
                  value: value === null || value === undefined ? null : value,
                  isActual: !useForecastStyle,
                  // Match financials bar labels: positive on top, negative on bottom.
                  label: {
                    position: Number.isFinite(numericValue) && numericValue < 0
                      ? 'bottom'
                      : 'top',
                  },
                  // Optional tooltip title (Portfolio maps studio_name → title).
                  title: row.title,
                  date: row.date,
                  tooltipHeaderRight,
                  itemStyle: {
                    color: barGradient,
                    opacity: useForecastStyle ? 0.3 : 1,
                  },
                };
              }),
            }],
          },
        });
      } catch (error) {
        console.error('Error fetching staff rate data:', error);
        notification.error({
          description: 'Failed to fetch staff rate data',
        });
        setStaffRateUpdateTime('');
      } finally {
        setStaffRateLoading(false);
      }
    };

    fetchStaffRate();
  }, [scope, studioId, activeDateTypeSegment, selectedMonth, formatMessage]);

  return {
    staffRateData,
    staffRateTableData,
    staffRateUpdateTime,
    staffRateLoading,
  };
};
